import { useCallback, useEffect, useRef, useState } from "react";

import { Tables } from "@/types/supabase-types";
import { Spinner } from "react-bootstrap";
import { Icon } from "@/components/Icon";
import { useUpdatePurchasingNoteMutation } from "./useUpdatePurchasingNoteMutation";

export type PurchasingNote = Tables<"PurchasingNote">;

export interface PurchasingNoteCellProps {
  /**
   * The initial note to display when the component is first loaded. Null indicates that there is no existing note for
   * this item/job combination.
   *
   * This is only used for the initial state, if the user edits or creates a new note, the component will manage its own
   * internal state without changing this prop value.
   */
  initialNote: PurchasingNote | null;

  jobId: number;
  itemId: number;
}
/**
 * Renders the purchasing note for a specific item/job combination. The note is rendered in a textarea that allows the
 * user to edit it and automatically save changes back to the database when the textarea loses focus.
 */
export const PurchasingNoteCell: React.FC<PurchasingNoteCellProps> = ({ initialNote, jobId, itemId }) => {
  const [note, setNote] = useState<PurchasingNote | null>(initialNote);
  const [input, setInput] = useState(initialNote?.value ?? "");
  const [saved, setSaved] = useState(false);
  const [savedTimeout, setSavedTimeout] = useState<NodeJS.Timeout | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const updateNoteMutation = useUpdatePurchasingNoteMutation();
  const saving = updateNoteMutation.isPending;
  const error = updateNoteMutation.error;

  // Update the input state value as the user types
  const onChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  }, []);

  // Define callback to save note on blur
  const onBlurAsync = useCallback(
    async (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Avoid saving again if a save is already in progress
      if (saving) return;

      // Reset some state at the start of a save operation
      setSaved(false);
      if (savedTimeout) {
        clearTimeout(savedTimeout);
        setSavedTimeout(null);
      }

      const newValue = e.target.value.trim();
      const result = await updateNoteMutation.mutateAsync({ note, newValue, itemId, jobId });

      setNote(result.newNote);
      setSaved(result.updated);
      if (result.updated) {
        setSavedTimeout(
          setTimeout(() => {
            setSaved(false);
          }, 1200),
        );
      }
    },
    [itemId, jobId, note, saving, savedTimeout, updateNoteMutation],
  );
  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      void onBlurAsync(e);
    },
    [onBlurAsync],
  );

  // This effect runs when the component mounts and whenever the input value changes
  useEffect(() => {
    // Do nothing if the textarea is not yet mounted
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Resize the textarea to fit its content, with a minimum height of the parent container
    // NOTE: We need to change the height to auto first so that it can shrink when content is removed
    const parent = textarea.parentElement!;
    const resize = () => {
      textarea.style.height = "auto";
      const height = Math.max(textarea.scrollHeight, parent.clientHeight);
      textarea.style.height = height.toString() + "px";
    };
    resize();

    // Listen for container resize
    const observer = new ResizeObserver(resize);
    observer.observe(textarea.parentElement!);
    return observer.disconnect.bind(observer);
  }, [input]);

  return (
    <>
      <textarea
        ref={textareaRef}
        className="form-control form-control-sm overflow-hidden"
        style={{ minWidth: 200, resize: "none" }}
        value={input}
        rows={2}
        onChange={onChange}
        onBlur={onBlur}
        disabled={saving}
        placeholder={"Add note..."}
      />
      {saving && (
        <>
          <Spinner size="sm" />
          <small className="text-secondary">Saving...</small>
        </>
      )}
      {saved && (
        <span className="text-success ms-1 align-middle">
          <Icon icon="check" className="text-success" />
          <small>Saved</small>
        </span>
      )}
      {error && <small className="text-danger">ERROR: {error.message}</small>}
    </>
  );
};
